import React, { useState, useRef } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";

interface NewsletterSubscriptionProps {
  variant?: "default" | "footer";
  className?: string;
}

const FOOTER_TURNSTILE_OPTIONS = { theme: "dark", size: "compact" } as const;

export const NewsletterSubscription = ({ variant = "default", className = "" }: NewsletterSubscriptionProps) => {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const { toast } = useToast();
  const subscribe = useAction(api.newsletter.subscribe);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (!token) {
      toast({
        title: "Security Check Required",
        description: "Please wait for the security check to complete.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await subscribe({ email, turnstileToken: token });

      if (result.status === "already_subscribed") {
        toast({
          title: "Already Subscribed",
          description: "You're already on our mailing list!",
        });
      } else {
        toast({
          title: "Successfully Subscribed!",
          description: "Thank you for joining our newsletter.",
        });
        setEmail("");
        // Reset turnstile after success
        turnstileRef.current?.reset();
        setToken("");
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Subscription Failed",
        description: "Something went wrong. Please try again later.",
        variant: "destructive",
      });
      // Reset turnstile after error
      turnstileRef.current?.reset();
      setToken("");
    } finally {
      setIsLoading(false);
    }
  };

  // We use dummy testing keys if environment variables aren't set yet
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

  if (variant === "footer") {
    return (
      <div className={`space-y-2 ${className}`}>
        <p className="font-roboto text-sm">Subscribe to our newsletter</p>
        <form onSubmit={handleSubscribe} className="flex flex-col space-y-2">
          <div className="flex space-x-2">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              aria-label="Email address for newsletter"
              aria-required="true"
              className="bg-white/10 border-white/20 text-foreground placeholder:text-muted-black"
              disabled={isLoading}
            />
            <Button type="submit" variant="default" size="sm" disabled={isLoading} aria-label="Subscribe to newsletter">
              {isLoading ? "..." : "Subscribe"}
            </Button>
          </div>
        </form>
        <div className="mt-3">
          {React.useMemo(() => (
            <Turnstile
              siteKey={siteKey}
              onSuccess={setToken}
              ref={turnstileRef}
              options={FOOTER_TURNSTILE_OPTIONS}
            />
          ), [siteKey])}
        </div>
      </div>
    );
  }

  return (
    <div className={`max-w-md ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Mail className="h-6 w-6 text-primary" />
        <h3 className="font-montserrat font-semibold text-lg">
          Subscribe to Our Newsletter
        </h3>
      </div>
      <p className="font-roboto text-sm text-muted-foreground mb-4">
        Stay updated with our latest programs, events, and opportunities.
      </p>
      <form onSubmit={handleSubscribe} className="flex flex-col space-y-4">
        <div className="flex space-x-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            aria-label="Email address for newsletter subscription"
            aria-required="true"
            className="flex-1"
            disabled={isLoading}
          />
          <Button type="submit" variant="cta" disabled={isLoading} aria-label="Submit newsletter subscription">
            {isLoading ? "Subscribing..." : "Subscribe"}
          </Button>
        </div>
      </form>
      <div className="mt-4 flex justify-center">
        {React.useMemo(() => (
          <Turnstile
            siteKey={siteKey}
            onSuccess={setToken}
            ref={turnstileRef}
          />
        ), [siteKey])}
      </div>
    </div>
  );
};
